import React, { useState, useEffect, useCallback, useRef } from "react";
import { Monitor, User, Clock, Eye, Check, ChevronDown, ExternalLink, Tv2, RefreshCw, Bell, AlertTriangle, RotateCcw, X, Grid } from "lucide-react";
import { useNavigate } from "react-router-dom";
import socket from "../../utils/socket.js";
import { apiGet, apiPost } from "../../utils/api.js";
import "./visualizer.css";

function formatLastSeen(date) {
  if (!date) return "Never";
  const diff = Math.floor((Date.now() - new Date(date)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const FILTER_OPTIONS = [
  { key: "all", label: "All Systems", dot: null },
  { key: "online", label: "Online Only", dot: "dot-green" },
  { key: "offline", label: "Offline Only", dot: "dot-red" },
];

// States where a new update trigger is allowed
const UPDATE_IDLE_STATES = new Set(["idle", "no_update", "success", "failed", "rollback_success", "rollback_failed", undefined]);

// Human-readable label for in-progress states
function updateStateLabel(state) {
  const labels = {
    checking: "Checking…",
    update_available: "Update found…",
    downloading: "Downloading…",
    verifying: "Verifying…",
    staging: "Staging…",
    installing: "Installing…",
    restarting: "Restarting…",
    already_running: "Already updating",
  };
  return labels[state] ?? null;
}

// ─── Send Notice modal ─────────────────────────────────────────
function NoticeModal({ system, onClose }) {
  const [message, setMessage] = useState("Come to the office room");
  const [sending, setSending] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const send = async () => {
    const trimmed = message.trim();
    if (!trimmed) return;
    setSending(true);
    try {
      await apiPost(`/api/agents/${system.agentId}/kiosk`, { message: trimmed });
    } finally {
      setSending(false);
      onClose();
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
    if (e.key === "Escape") onClose();
  };

  return (
    <div className="nmodal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="nmodal">
        <div className="nmodal-header">
          <span className="nmodal-title">Send Notice</span>
          <button className="nmodal-close" onClick={onClose}>✕</button>
        </div>
        <div className="nmodal-sub">To: <strong>{system.systemName}</strong>{system.employeeName ? ` · ${system.employeeName}` : ""}</div>
        <textarea
          ref={inputRef}
          className="nmodal-input"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={onKey}
          rows={3}
          maxLength={300}
          placeholder="Type your message…"
        />
        <div className="nmodal-footer">
          <button className="nmodal-btn nmodal-btn--cancel" onClick={onClose}>Cancel</button>
          <button className="nmodal-btn nmodal-btn--send" onClick={send} disabled={sending || !message.trim()}>
            {sending ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Windows-style right-click context menu ────────────────────
function ContextMenu({ system, pos, onClose, updateState, onSendNotice }) {
  const navigate = useNavigate();
  const ref = useRef(null);

  useEffect(() => {
    const down = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const key = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", down);
    document.addEventListener("keydown", key);
    return () => { document.removeEventListener("mousedown", down); document.removeEventListener("keydown", key); };
  }, [onClose]);

  // Flip if menu would overflow right/bottom edge
  const style = {
    top: Math.min(pos.y, window.innerHeight - 280),
    left: Math.min(pos.x, window.innerWidth - 220),
  };

  const run = (fn) => { fn(); onClose(); };

  const menuItems = [
    {
      icon: <ExternalLink size={14} />,
      label: "Open Device Page",
      action: () => navigate(`/devices/${system.agentId}`),
    },
    {
      icon: <Tv2 size={14} />,
      label: "Remote View",
      action: () => navigate(`/screen/${system.agentId}`),
      disabled: system.status !== "online",
    },
    { separator: true },
    {
      icon: <Bell size={14} />,
      label: "Send Notice",
      action: () => onSendNotice(system),
      disabled: system.status !== "online",
    },
    {
      icon: <RefreshCw size={14} />,
      label: updateStateLabel(updateState) ?? "Force Update",
      action: () => apiPost(`/api/agents/${system.agentId}/update-now`, {}),
      disabled: system.status !== "online" || !UPDATE_IDLE_STATES.has(updateState),
    },
    {
      icon: <RotateCcw size={14} />,
      label: "Restart Agent",
      action: () => apiPost(`/api/agents/${system.agentId}/agent-restart`, {}),
      disabled: system.status !== "online",
      danger: true,
    },
    { separator: true },
    {
      icon: <AlertTriangle size={14} />,
      label: `${system.threatCount || 0} Active Alert${system.threatCount !== 1 ? "s" : ""}`,
      action: () => navigate(`/devices/${system.agentId}`),
      muted: !system.hasThreat,
    },
  ];

  return (
    <div className="ctx-menu" style={style} ref={ref}>
      <div className="ctx-menu-title">
        <div className="ctx-menu-title-name">{system.systemName}</div>
        {system.employeeName && <div className="ctx-menu-title-sub">{system.employeeName}</div>}
        <div className={`ctx-menu-title-status ${system.status === "online" ? "status-online" : "status-offline"}`}>
          <span className="ctx-status-dot" />
          {system.status === "online" ? "Online" : "Offline"} · {formatLastSeen(system.lastSeen)}
        </div>
      </div>
      <div className="ctx-separator" />
      {menuItems.map((item, i) =>
        item.separator ? (
          <div key={i} className="ctx-separator" />
        ) : (
          <button
            key={i}
            className={`ctx-item ${item.danger ? "ctx-item--danger" : ""} ${item.muted ? "ctx-item--muted" : ""} ${item.disabled ? "ctx-item--disabled" : ""}`}
            onClick={() => !item.disabled && run(item.action)}
            disabled={item.disabled}
          >
            <span className="ctx-item-icon">{item.icon}</span>
            <span className="ctx-item-label">{item.label}</span>
          </button>
        )
      )}
    </div>
  );
}

// ─── Visibility dropdown ───────────────────────────────────────
function VisibilityMenu({ filter, onSelect, onlineCount, offlineCount }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const active = FILTER_OPTIONS.find((f) => f.key === filter);
  const counts = { all: onlineCount + offlineCount, online: onlineCount, offline: offlineCount };

  return (
    <div className="vmenu-wrap" ref={ref}>
      <button className={`vmenu-trigger ${open ? "vmenu-trigger--open" : ""}`} onClick={() => setOpen((v) => !v)}>
        <Eye size={14} />
        <span>{active.label}</span>
        <ChevronDown size={13} className={`vmenu-chevron ${open ? "vmenu-chevron--up" : ""}`} />
      </button>

      {open && (
        <div className="vmenu-dropdown">
          <div className="vmenu-header">Visibility</div>
          <div className="vmenu-separator" />
          {FILTER_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              className={`vmenu-item ${filter === opt.key ? "vmenu-item--active" : ""}`}
              onClick={() => { onSelect(opt.key); setOpen(false); }}
            >
              <span className="vmenu-check">{filter === opt.key && <Check size={12} strokeWidth={2.5} />}</span>
              {opt.dot && <span className={`vmenu-dot ${opt.dot}`} />}
              <span className="vmenu-label">{opt.label}</span>
              <span className="vmenu-count">{counts[opt.key]}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── System card ───────────────────────────────────────────────
function SystemCard({ system, onContextMenu }) {
  const isOnline = system.status === "online";

  const handleClick = (e) => {
    e.stopPropagation();
    onContextMenu(system, { x: e.clientX, y: e.clientY });
  };

  const handleUpdate = (e) => {
    e.stopPropagation();
    apiPost(`/api/agents/${system.agentId}/update-now`, {});
  };

  return (
    <div
      className={`viz-card ${isOnline ? "viz-card--online" : "viz-card--offline"} ${system.isOnLeave ? "viz-card--leave" : ""}`}
      onClick={handleClick}
    >
      {system.hasThreat && (
        <div className="viz-badge viz-badge--threat">{system.threatCount > 9 ? "9+" : system.threatCount}</div>
      )}
      {system.notificationCount > 0 && (
        <div className="viz-badge viz-badge--notify">{system.notificationCount > 9 ? "9+" : system.notificationCount}</div>
      )}

      <div className={`viz-status-dot ${isOnline ? "dot--online" : "dot--offline"}`} />

      <div className="viz-icon-wrap">
        <Monitor size={32} strokeWidth={1.5} className="viz-monitor-icon" />
      </div>

      <div className="viz-labels">
        <span className="viz-system-name" title={system.systemName}>{system.systemName}</span>
        {system.employeeName && (
          <span className="viz-employee-name" title={system.employeeName}>
            <User size={10} /> {system.employeeName}
          </span>
        )}
      </div>

      {system.isOnLeave && (
        <div className="viz-leave-overlay"><span>On Leave</span></div>
      )}

      {system.needsUpdate && isOnline && (
        <button className="viz-update-btn" onClick={handleUpdate} title={`Update available: ${system.latestVersion}`}>
          ↑ Update
        </button>
      )}
    </div>
  );
}

// ─── CCTV tile: one WebRTC stream per device ───────────────────
function CCTVTile({ agentId, label }) {
  const videoRef = useRef(null);
  const pcRef = useRef(null);
  const sessionIdRef = useRef(null);
  const pendingCandidates = useRef([]);
  const rdSet = useRef(false);
  const [status, setStatus] = useState("Connecting…");
  const [live, setLive] = useState(false);

  useEffect(() => {
    let disposed = false;

    const closePeer = () => {
      try { pcRef.current?.close(); } catch { /* */ }
      pcRef.current = null;
    };

    const makePc = () => {
      const stun = import.meta.env.VITE_STUN_URL || "stun:stun.l.google.com:19302";
      const servers = [{ urls: stun }];
      const turnUrl = import.meta.env.VITE_TURN_URL;
      if (turnUrl) servers.push({ urls: Array.isArray(turnUrl) ? turnUrl : [turnUrl], username: import.meta.env.VITE_TURN_USERNAME || "", credential: import.meta.env.VITE_TURN_CREDENTIAL || "" });
      const pc = new RTCPeerConnection({ iceServers: servers });
      pc.ontrack = (e) => {
        const [stream] = e.streams || [];
        if (videoRef.current && stream) videoRef.current.srcObject = stream;
        setStatus("Live"); setLive(true);
      };
      pc.onicecandidate = (e) => {
        if (e.candidate && sessionIdRef.current)
          socket.emit("screen:ice_candidate", { sessionId: sessionIdRef.current, candidate: e.candidate });
      };
      pc.onconnectionstatechange = () => {
        if (pc.connectionState === "failed" || pc.connectionState === "closed") {
          setStatus("Disconnected"); setLive(false);
        }
      };
      pcRef.current = pc;
      return pc;
    };

    const onOffer = async ({ sessionId, sdp }) => {
      try {
        if (sessionIdRef.current && sessionId !== sessionIdRef.current) return;
        sessionIdRef.current = sessionId;
        const pc = pcRef.current || makePc();
        await pc.setRemoteDescription(new RTCSessionDescription(sdp));
        rdSet.current = true;
        while (pendingCandidates.current.length) {
          try { await pc.addIceCandidate(new RTCIceCandidate(pendingCandidates.current.shift())); } catch { /* */ }
        }
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        socket.emit("screen:answer", { sessionId, sdp: answer });
        setStatus("Negotiating…");
      } catch { setStatus("Error"); }
    };

    const onIce = async ({ sessionId, candidate }) => {
      if (sessionIdRef.current && sessionId !== sessionIdRef.current) return;
      if (!rdSet.current) { pendingCandidates.current.push(candidate); return; }
      try { await pcRef.current?.addIceCandidate(new RTCIceCandidate(candidate)); } catch { /* */ }
    };

    const onEnded = ({ sessionId }) => {
      if (sessionIdRef.current && sessionId !== sessionIdRef.current) return;
      setStatus("Ended"); setLive(false); closePeer();
    };

    makePc();
    socket.on("screen:offer", onOffer);
    socket.on("screen:ice_candidate", onIce);
    socket.on("screen:session_ended", onEnded);

    socket.emit("screen:view_request", { agentId, stealth: true }, (resp) => {
      if (disposed) return;
      if (!resp?.success) { setStatus("Unavailable"); return; }
      sessionIdRef.current = resp.sessionId;
      setStatus("Waiting for stream…");
    });

    return () => {
      disposed = true;
      socket.off("screen:offer", onOffer);
      socket.off("screen:ice_candidate", onIce);
      socket.off("screen:session_ended", onEnded);
      if (sessionIdRef.current)
        socket.emit("screen:end", { sessionId: sessionIdRef.current, reason: "viewer_unmounted" });
      try {
        if (videoRef.current?.srcObject) {
          videoRef.current.srcObject.getTracks().forEach((t) => t.stop());
          videoRef.current.srcObject = null;
        }
      } catch { /* */ }
      closePeer();
      pendingCandidates.current = [];
      rdSet.current = false;
    };
  }, [agentId]);

  return (
    <div className="cctv-tile">
      <video ref={videoRef} autoPlay playsInline muted className="cctv-video" />
      {!live && <div className="cctv-status">{status}</div>}
      <div className="cctv-label">{label}</div>
    </div>
  );
}

// ─── CCTV full-screen grid overlay ────────────────────────────
function CCTVGrid({ systems, onClose }) {
  const online = systems.filter((s) => s.status === "online");

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="cctv-overlay">
      <div className="cctv-bar">
        <span className="cctv-bar-title">
          <Grid size={16} /> Live CCTV — {online.length} device{online.length !== 1 ? "s" : ""}
        </span>
        <button className="cctv-close" onClick={onClose}><X size={20} /></button>
      </div>
      {online.length === 0 ? (
        <div className="cctv-empty"><Monitor size={48} strokeWidth={1} /><p>No online devices to display.</p></div>
      ) : (
        <div className="cctv-grid">
          {online.map((s) => (
            <CCTVTile key={s.agentId} agentId={s.agentId} label={s.systemName || s.agentId} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────
export default function Visualizer() {
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [ctxMenu, setCtxMenu] = useState(null); // { system, pos }
  const [agentUpdateStates, setAgentUpdateStates] = useState({}); // agentId → state string
  const [noticeTarget, setNoticeTarget] = useState(null); // system to send notice to
  const [showCCTV, setShowCCTV] = useState(false);

  const fetchSystems = useCallback(() => {
    apiGet("/api/visualizer-data")
      .then((res) => res.json())
      .then((data) => { if (Array.isArray(data)) setSystems(data); })
      .catch((err) => console.error("[visualizer] fetch error:", err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchSystems();
    const handleAgentUpdate = (data) => {
      if (!data?.agentId) return;
      setSystems((prev) =>
        prev.map((s) => s.agentId === data.agentId ? { ...s, status: data.status ?? s.status } : s)
      );
    };
    const handleUpdateStatus = (data) => {
      if (!data?.agentId) return;
      setAgentUpdateStates((prev) => ({ ...prev, [data.agentId]: data.state }));
    };
    socket.on("agent_update", handleAgentUpdate);
    socket.on("agent_update_status", handleUpdateStatus);
    return () => {
      socket.off("agent_update", handleAgentUpdate);
      socket.off("agent_update_status", handleUpdateStatus);
    };
  }, [fetchSystems]);

  const displayed = systems.filter((s) => {
    if (filter === "online") return s.status === "online";
    if (filter === "offline") return s.status === "offline";
    return true;
  });

  const onlineCount = systems.filter((s) => s.status === "online").length;
  const offlineCount = systems.filter((s) => s.status === "offline").length;

  return (
    <div className="viz-page" onClick={() => setCtxMenu(null)}>
      <div className="viz-header">
        <div className="viz-header-left">
          <h1 className="viz-title">Office Visualizer</h1>
          <div className="viz-counts">
            <span className="count-badge count-online">{onlineCount} online</span>
            <span className="count-badge count-offline">{offlineCount} offline</span>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <button className="cctv-launch-btn" onClick={() => setShowCCTV(true)} title="Live CCTV Grid">
            <Grid size={15} /> CCTV View
          </button>
          <VisibilityMenu filter={filter} onSelect={setFilter} onlineCount={onlineCount} offlineCount={offlineCount} />
        </div>
      </div>

      {loading ? (
        <div className="viz-loading">Loading systems…</div>
      ) : displayed.length === 0 ? (
        <div className="viz-empty">
          <Monitor size={48} strokeWidth={1} />
          <p>{filter === "all" ? "No registered systems found." : `No ${filter} systems.`}</p>
        </div>
      ) : (
        <div className="viz-grid">
          {displayed.map((system) => (
            <SystemCard
              key={system.agentId}
              system={system}
              onContextMenu={(sys, pos) => setCtxMenu({ system: sys, pos })}
            />
          ))}
        </div>
      )}

      {ctxMenu && (
        <ContextMenu
          system={ctxMenu.system}
          pos={ctxMenu.pos}
          onClose={() => setCtxMenu(null)}
          updateState={agentUpdateStates[ctxMenu.system.agentId]}
          onSendNotice={(sys) => { setCtxMenu(null); setNoticeTarget(sys); }}
        />
      )}

      {noticeTarget && (
        <NoticeModal system={noticeTarget} onClose={() => setNoticeTarget(null)} />
      )}

      {showCCTV && (
        <CCTVGrid systems={systems} onClose={() => setShowCCTV(false)} />
      )}
    </div>
  );
}
